function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { format, isSameDay, startOfMonth } from 'date-fns';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ConditionalWrapper } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import GenericError from "../../../components/GenericError/GenericError";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { useLocaleKeys } from "../../../i18n";
import { HistoricAgreementStatsModel } from "../../../models";
import { RelationshipProgressStatus } from "../../../models/relationship";
import { AgreementStatus } from "../../../models/relationship-summary";
import { toDateStampObject } from "../../../models/time";
import { selectors } from "../../../store/state-selectors";
import { utils } from "../../../utils";
import { getLastXMonths } from "../../../utils/date.utils";
import { getXAxisTextForQuarterlyDate } from "../charts/charts.utils";
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from "../charts/commonChartProps";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import ThreeLineTopCard from "../GraphWidget/ThreeLineTopCard";
import styles from "./HistoricAgreementStatsWidget.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const agreementsColor = colors.blue700;
const proposalsColor = colors.blue500;
const stackId = 'a';
const legendItems = [{
  label: 'Active agreements',
  color: agreementsColor
}, {
  label: 'Proposals awaiting approval',
  color: proposalsColor
}];
const card1Props = {
  testid: 'agreements-summary',
  title: 'Active agreements',
  success: true,
  titleInfo: 'The number of signed agreements that are neither terminated nor completed.',
  secondaryInfo: 'The monthly recurring revenue accounts only for fixed-price services that are automatically billed.'
};
const card2Props = {
  testid: 'proposals-summary',
  title: 'Awaiting approval',
  titleInfo: 'The number of proposals that are awaiting your clients approval.',
  secondaryInfo: 'The monthly recurring revenue accounts only for fixed-price services that are automatically billed. If packages are offered, the package with the lowest value is used in the calculation and optional services, if offered, are excluded from the calculation.'
};
export function fillLast12Months(data, now) {
  return getLastXMonths(now, 12).map(date => data.find(item => isSameDay(startOfMonth(date), startOfMonth(item.date()))) || HistoricAgreementStatsModel.makeEmpty({
    date
  }));
}
function getTooltipItems(payload) {
  if (!payload || !payload.length) return;
  const historicAgreementStatsModel = payload[0].payload;
  return [{
    label: "".concat(utils.formatNumberWithCommas(historicAgreementStatsModel.proposalsCount()), " Proposals awaiting approval"),
    color: proposalsColor
  }, {
    label: "".concat(utils.formatNumberWithCommas(historicAgreementStatsModel.agreementsCount()), " Active agreements"),
    color: agreementsColor
  }];
}
function getTitle(label, payload) {
  if (!payload || !payload.length) return '';
  const historicAgreementStatsModel = payload[0].payload;
  return format(historicAgreementStatsModel.date(), 'MMMM');
}
function HistoricAgreementStatsChart(_ref) {
  let {
    testid,
    data,
    isTestMode
  } = _ref;
  const {
    getNow
  } = useIoContext();
  const barChartProps = isTestMode ? {
    height: 100,
    width: 100
  } : {};
  const fullData = useMemo(() => fillLast12Months(data, getNow()), [data]);
  return /*#__PURE__*/_jsx("div", {
    "data-testid": testid,
    className: styles.chart,
    children: /*#__PURE__*/_jsx(ConditionalWrapper, {
      condition: !isTestMode,
      wrapper: children => /*#__PURE__*/_jsx(ResponsiveContainer, {
        children: children
      }),
      children: /*#__PURE__*/_jsxs(BarChart, _objectSpread(_objectSpread({
        data: fullData
      }, barChartProps), {}, {
        barCategoryGap: "10%",
        margin: {
          top: 20,
          left: 16,
          right: 16
        },
        children: [/*#__PURE__*/_jsx(CartesianGrid, _objectSpread({}, commonCartesianGridProps)), /*#__PURE__*/_jsx(Legend, {
          content: /*#__PURE__*/_jsx(CustomLegend, {
            testid: "".concat(testid, "-legend"),
            items: legendItems
          })
        }), /*#__PURE__*/_jsx(XAxis, _objectSpread(_objectSpread({}, commonXAxisProps), {}, {
          dataKey: item => item.date(),
          height: 49,
          tick: /*#__PURE__*/_jsx(CustomXAxisTick, {
            tickHeight: 30,
            getText: getXAxisTextForQuarterlyDate
          })
        })), /*#__PURE__*/_jsx(YAxis, {
          axisLine: false,
          tickLine: false,
          width: 32,
          tickFormatter: value => value === 0 ? '' : utils.formatNumberWithCommas(value),
          fontSize: 12
        }), /*#__PURE__*/_jsx(Tooltip, {
          cursor: false,
          isAnimationActive: false,
          content: /*#__PURE__*/_jsx(CustomTooltip, {
            skin: "dark",
            testid: "".concat(testid, "-tooltip"),
            getTitle: getTitle,
            getItems: getTooltipItems
          })
        }), /*#__PURE__*/_jsx(Bar, _objectSpread(_objectSpread({}, commonBarProps), {}, {
          dataKey: item => item.agreementsCount(),
          stackId: stackId,
          fill: agreementsColor,
          shape: /*#__PURE__*/_jsx(CustomBarShape, {
            getTestId: item => "agreements-for-".concat(toDateStampObject(item.date()).month)
          })
        })), /*#__PURE__*/_jsx(Bar, _objectSpread(_objectSpread({}, commonBarProps), {}, {
          dataKey: item => item.proposalsCount(),
          stackId: stackId,
          fill: proposalsColor,
          shape: /*#__PURE__*/_jsx(CustomBarShape, {
            hasRadius: true,
            getTestId: item => "proposals-for-".concat(toDateStampObject(item.date()).month)
          })
        }))]
      }))
    })
  });
}
function PlaceholderChart(props) {
  const {
    getNow
  } = useIoContext();
  const placeholderData = useMemo(() => {
    const emptyData = fillLast12Months([], getNow());
    return emptyData.map((item, idx) => item.setActiveAgreementsCount(Math.floor(idx / 2) + 3).setProposalsCount(3));
  }, []);
  return /*#__PURE__*/_jsx(HistoricAgreementStatsChart, _objectSpread({
    testid: "agreement-stats-chart-placeholder",
    data: placeholderData
  }, props));
}
const Layout = _ref2 => {
  let {
    loading,
    children
  } = _ref2;
  const {
    t
  } = useLocaleKeys('pages.dashboard.historicAgreementStatsWidget');
  return /*#__PURE__*/_jsx(GraphWidgetLayout, {
    title: t.title(),
    loading: loading,
    bottomHeight: "l",
    children: children
  });
};
export default function HistoricAgreementStatsWidget(props) {
  const {
    fetchHistoricAgreementStats
  } = useStoreActions();
  const initialData = useStore(state => state.historicAgreementStats.data);
  const hasSentAProposal = useStore(selectors.hasSentAProposal);
  const isMemberUser = useStore(selectors.user.isMemberUser);
  const loadingOrEmptyBg = () => /*#__PURE__*/_jsx(PlaceholderChart, _objectSpread({}, props));
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    fetch: fetchHistoricAgreementStats,
    initialData: initialData,
    renderChild: _ref3 => {
      let {
        data,
        loading
      } = _ref3;
      const isEmpty = data.length === 0;
      const firstItem = isEmpty ? HistoricAgreementStatsModel.makeEmpty() : data[0];
      return /*#__PURE__*/_jsxs(Layout, {
        loading: loading,
        children: [/*#__PURE__*/_jsx(ThreeLineTopCard, _objectSpread(_objectSpread({}, card1Props), {}, {
          primaryText: firstItem.agreementsCount(),
          secondaryText: isMemberUser ? undefined : "".concat(firstItem.agreementsDollarAmount(), " monthly recurring"),
          linkTo: "".concat(routes.agreementsDashboard, "?agreementStatus=").concat(AgreementStatus.Active)
        })), /*#__PURE__*/_jsx(ThreeLineTopCard, _objectSpread(_objectSpread({}, card2Props), {}, {
          primaryText: firstItem.proposalsCount(),
          secondaryText: isMemberUser ? undefined : "".concat(firstItem.proposalsDollarAmount(), " monthly recurring"),
          linkTo: "".concat(routes.proposalsDashboard, "?filter=").concat(RelationshipProgressStatus.PaymentMethod, ",").concat(RelationshipProgressStatus.Reviewed, ",").concat(RelationshipProgressStatus.Sent)
        })), /*#__PURE__*/_jsx(GraphWidgetBottomCard, {
          empty: isEmpty && !hasSentAProposal,
          emptyMsg: "Here you will view your agreements pipeline over time",
          "data-testid": "agreement-stats",
          loadingOrEmptyBg: loadingOrEmptyBg,
          children: /*#__PURE__*/_jsx(HistoricAgreementStatsChart, _objectSpread({
            testid: "agreement-stats-chart",
            data: data
          }, props))
        })]
      });
    },
    renderLoader: () => /*#__PURE__*/_jsxs(Layout, {
      loading: true,
      children: [/*#__PURE__*/_jsx(ThreeLineTopCard, _objectSpread(_objectSpread({}, card1Props), {}, {
        loading: true
      })), /*#__PURE__*/_jsx(ThreeLineTopCard, _objectSpread(_objectSpread({}, card2Props), {}, {
        loading: true
      })), /*#__PURE__*/_jsx(GraphWidgetBottomCard, {
        loading: true,
        loadingMsg: "Loading data...",
        loadingOrEmptyBg: loadingOrEmptyBg
      })]
    }),
    renderError: () => /*#__PURE__*/_jsx(GenericError, {
      testid: "widget-error"
    })
  });
}